import { mdiCheck, mdiClose, mdiPencil, mdiDelete, mdiPlus } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  itemStockOutwardDefaultValue,
  OutwardSource,
  OutwardType,
  StockOutwardItemMovementModel,
  UniqueProductModel,
} from "../models/stock_transfer_model";
import { WarehouseModel } from "../models/warehouse_model";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import { generate_id } from "../journals/destination_stock_voucher/components/item_row_status";
import { fetchGet } from "../../../service/api_client";
import ModalDialog from "../../../ui/new_components/common/modal";
import { ItemModelV2 } from "../models/item_model";
import NewItemSearchByIdField from "../items/item_search_by_ids";
import POItemSearchFieldV2 from "../../purchase_order/purchase_order_create/components/po-item-search-widget";
import Papa from "papaparse";
import { ScannedBarCodeScanForDeviceFromPTAPI } from "../stock_transfer/sender_stock_transfer/service/stock_transfer_repo";

interface ItemRowProps {
  component?: StockOutwardItemMovementModel;
  onSubmit: (data: StockOutwardItemMovementModel) => void;
  edit: boolean;
  disabled: boolean;
  onDelete?: (i: StockOutwardItemMovementModel) => void;
  existingItems: StockOutwardItemMovementModel[];
  availableItems?: {
    item_id: string;
    warehouse_id: string;
    available_units: number;
  }[];
  from_warehouse: WarehouseModel;
  source: string;
  outward_type: string;
}

const ItemRowOutward: FunctionComponent<ItemRowProps> = (props) => {
  const [data, setData] = useState<StockOutwardItemMovementModel>({
    ...itemStockOutwardDefaultValue,
  });

  // const [selectedItem, setSelectedItem] = useState<string>();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const { edit, onSubmit, existingItems, availableItems } = props;
  const [editable, setEditable] = useState(false);
  const [addProduct, setAddProduct] = useState(false);
  const [addProduct1, setAddProduct1] = useState(false);
  const [check, setMdiCheck] = useState(true);
  const [totalUnits, setTotalUnits] = useState(0);
  const [prevUnits, setPrevUnits] = useState(0);
  const location = useLocation();
  const isCreatePath = location.pathname.endsWith("/create");
  const [selectedRows, setSelectedRows] = useState<boolean[]>(
    new Array(data.uniqueIds?.length).fill(false)
  );
  const [selectAll, setSelectAll] = useState(false);
  const uniqueIds = useRef<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const itemIdsRef = useRef<any[]>([]);
  const alreadyAddedIds = useRef<string[]>([]);
  const itemIds = availableItems?.map((item: any) => item.item_id) || [];
  const [scannedDevices, setScannedDevices] = useState<UniqueProductModel[]>(
    []
  );
  const [scan, setScan] = useState<boolean>(false);
  const [scannedID, setScannedID] = useState<string>("");
  const [choiceModal, setChoiceModal] = useState<boolean>(false);
  const [scanTimeout, setScanTimeout] = useState<NodeJS.Timeout | null>(null);
  const [scanBuffer, setScanBuffer] = useState<string>("");


  useEffect(() => {
    itemIdsRef.current = (availableItems ?? []).map(
      (item: any) => item.item_id
    );
    if ((props.component?.uniqueIds?.length ?? 0) > 0) {
      alreadyAddedIds.current = (props.component?.uniqueIds ?? [])
        .map((u: any) => u.unique_id?.trim())
        .filter(Boolean);
    } else {
      alreadyAddedIds.current = [];
    }
    setEditable(edit);
    if (props.component) {
      setData(props.component);
    } else {
      setData({ ...itemStockOutwardDefaultValue });
    }
  }, [edit, props.component, availableItems]);

  // useEffect(() => {
  //   if (props.component) {
  //     setData((prevData) => ({
  //       ...prevData,
  //       ...props.component,
  //     }));
  //   }
  // }, [props.component]);

  const scannedInputFieldRef = useRef<HTMLInputElement>(null);
  const isProcessingScan = useRef(false);
  const scannedBufferRef = useRef<string>("");
  const inputTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // const [scan, setScan] = useState<boolean>(false);
  useEffect(() => {
    if (scan && scannedInputFieldRef.current && choiceModal) {
      // Clear the input and focus when scan mode is activated
      setScannedID("");
      scannedBufferRef.current = "";
      isProcessingScan.current = false;

      // Clear any pending timeout
      if (inputTimeoutRef.current) {
        clearTimeout(inputTimeoutRef.current);
      }

      // Use setTimeout to ensure the input is rendered before focusing
      setTimeout(() => {
        if (scannedInputFieldRef.current) {
          scannedInputFieldRef.current.focus();
          scannedInputFieldRef.current.select();
        }
      }, 100);
    }
  }, [scan, choiceModal]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (inputTimeoutRef.current) {
        clearTimeout(inputTimeoutRef.current);
      }
    };
  }, []);

  const [initialRows, setInitialRows] = useState<
    { id: string; unique_id: string; item_name: string }[]
  >([]);

  useEffect(() => {
    if (!data.item?.id || !props.from_warehouse?.id) return;

    const available = availableItems?.find(
      (item: any) =>
        item.item_id === data.item.id &&
        item.warehouse_id === props.from_warehouse.id
    );

    const availableUnits = available?.available_units ?? 0;

    if (props.source === OutwardSource["Stock inventory"]) {
      setTotalUnits(availableUnits + prevUnits);
    }
  }, [availableItems, data.item?.id, props.from_warehouse?.id, props.source]);

  useEffect(() => {
    // Find the existing item in the list
    const existingItem = existingItems.find((item) => item.id === data.id);

    // Get the existing unit_no, defaulting to 0 if not found
    const existingUnitNo = existingItem ? Number(existingItem.unit_no) || 0 : 0;

    // Update state with the existing unit_no
    setPrevUnits(existingUnitNo);
  }, [existingItems, data.id]); // Track changes in existingItems and data.id

  useEffect(() => {
    if (props.source === OutwardSource["Stock inventory"]) {
      if (!data.unit_no) return; // Ensure unit_no is valid
      if (
        data.item?.subcategory !== "Finished Goods" &&
        data.item?.subcategory !== "IT and Electronic Hardware"
      ) {
        return;
      }
      if (!data.unit_no) return; // Ensure unit_no is valid

      const rows = Array.from({ length: data.unit_no }, (_, index) => {
        const existingUniqueId = data.uniqueIds?.[index]?.unique_id || ""; // Get existing unique_id if present

        return {
          id: `${"OUT"}-${generate_id(8, "numeric")}`, // Unique ID generation
          unique_id: existingUniqueId,
          item_name: data.item?.name || "",
        };
      });

      setInitialRows(rows);
      setData((prevData) => ({ ...prevData, uniqueIds: rows }));
    }
  }, [data.unit_no, props.source]); // Depend on data.unit_no to update rows dynamically

  // Use data.uniqueIds as the source of truth
  const handleInputChange = (index: number, value: string) => {
    const updatedUniqueIds: UniqueProductModel[] = Array.isArray(data.uniqueIds)
      ? [...data.uniqueIds]
      : [];
    updatedUniqueIds[index].unique_id = value;
    setData({ ...data, uniqueIds: updatedUniqueIds });
  };

  useEffect(() => {
    if (data.item?.item_code && props.from_warehouse?.name) {
      checkUniqueIdsExists();
      if (scannedID) {
        getDeviceIDs(scannedID);
      }
    }
  }, [data.item?.item_code, props.from_warehouse?.name, scannedID]);

  const checkUniqueIdsExists = async () => {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items/unique-ids?warehouse_id=${props.from_warehouse?.id}&item_code=${data.item?.item_code}`;
    setLoading(true);

    try {
      const res = await fetchGet(url);
      if (res.success) {
        // Later in checkUniqueIdsExists:
        // uniqueIds.current = res.result?.map((item: any) => item.unique_id);
        const fetchedIds =
          res.result
            ?.map((item: any) => item.unique_id?.trim())
            .filter(Boolean) ?? [];

        const combined = [...fetchedIds, ...(alreadyAddedIds.current ?? [])];

        // Remove duplicates using Set
        const uniqueCombined = Array.from(new Set(combined));

        // Update refs
        uniqueIds.current = uniqueCombined;
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching uniqueIds",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getDeviceIDs = async (scanned_id: string) => {
    try {
      if (!scanned_id || scanned_id.trim() === "") {
        return false;
      }
      if (scanned_id.length !== 6 && scanned_id.length !== 8) {
        showToast({
          type: ToastType.error,
          text: "Please enter a valid scanned ID (6 or 8 characters)",
        });
        return false;
      }
      setLoading(true);
      const res = await ScannedBarCodeScanForDeviceFromPTAPI(
        data.item?.item_code,
        // props.component?.item?.item_code!,
        props.from_warehouse.id,
        scanned_id.trim()
      );
      if (res.success) {
        setScannedDevices((prev) => {
          const existing = new Set(prev.map((d) => d.unique_id));
          const newOnes = res.devices.filter(
            (d: any) => !existing.has(d.unique_id)
          );
          if (newOnes.length === 0) {
            showToast({
              type: ToastType.warning,
              text: "No devices found/All scanned devices are already added.",
            });
            return prev;
          }
          showToast({
            type: ToastType.success,
            text: `Added ${newOnes.length} new device(s).`,
          });
          return [...prev, ...newOnes];
        });
        setScannedID("");
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
      setLoading(false);
      return true;
    } catch (error: any) {
      setLoading(false);
      showToast({
        type: ToastType.error,
        text: error.message,
      });
      return false;
    }
  };

  const handleAddingDeviceIds = () => {
    setData((prev) => ({
      ...prev,
      uniqueIds: scannedDevices,
      unit_no: scannedDevices.length,
    }));
    if (!Array.isArray(scannedDevices) || scannedDevices.length === 0) {
      showToast({ type: ToastType.error, text: "No devices to add." });
      return;
    }

    for (const dev of scannedDevices) {
      if (!dev.unique_id || dev.unique_id.trim() === "") {
        showToast({
          type: ToastType.error,
          text: "Invalid unique ID detected.",
        });
        return;
      }
      if (!uniqueIds.current.includes(dev.unique_id)) {
        showToast({
          type: ToastType.error,
          text: `Unique ID does not exist for ${dev.unique_id}`,
        });
        return;
      }
    }

    // data.uniqueIds = scannedDevices;
    setAddProduct(false);
    setScan(false);
    setChoiceModal(false);
  };

  const handleDeleteDevice = (device_id: string) => {
    setScannedDevices((prev) => prev.filter((d) => d.unique_id !== device_id));
  };

  // Add new row
  const handleAdd = () => {
    const newRow: UniqueProductModel = {
      unique_id: "",
      item_name: data.item?.name || "",
    };

    setData({
      ...data,
      uniqueIds: [...(data.uniqueIds || []), newRow],
    });
  };

  // Delete row
  const handleDelete = (index: number) => {
    const updatedUniqueIds = [...(data.uniqueIds || [])];
    updatedUniqueIds.splice(index, 1);
    setData({ ...data, uniqueIds: updatedUniqueIds });
  };

  const handleItemSelect = async (selectedItem: ItemModelV2) => {
    if (props.from_warehouse?.name === undefined) {
      showToast({
        type: ToastType.warning,
        text: "Please select from warehouse before entering item.",
      });
      return;
    }
    if (data.unit_no) {
      setData((prevData) => ({
        ...prevData,
        unit_no: 0, // Reset units when a new item is selected
      }));
    }

    // Find available units from availableItems using selected item ID and warehouse ID
    const available = availableItems?.find(
      (item: any) =>
        item.item_id === selectedItem.id &&
        item.warehouse_id === props.from_warehouse?.id
    );

    const availableUnits = available?.available_units ?? 0;
    setTotalUnits(availableUnits + prevUnits); // <-- Set available units here

    setData((prevData) => ({
      ...prevData,
      id: generate_id(),
      item: selectedItem,
      item_id: selectedItem.id,
      item_code: selectedItem.item_code,
      unit_measure: selectedItem.unit?.formal_name,
    }));
  };

  useEffect(() => {
    if (data?.inTransit_id && props?.from_warehouse?.name) {
      handleIntransit();
    }
  }, [data?.inTransit_id, props?.from_warehouse?.name]);

  const handleIntransit = async () => {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items/intransit?from_warehouse=${props.from_warehouse?.name}&in_transit_id=${data.inTransit_id}`;
    setLoading(true);

    try {
      const res = await fetchGet(url);

      if (res.success) {
        const { item_data } = res.data;
        const filteredUniqueIds = item_data?.uniqueIds?.filter(
          (u: any) => u.status === ""
        );

        setData((prev) => ({
          ...prev,
          stock_transfer_id: item_data?.stock_transfer_id,
          item_code: item_data?.item_code,
          item: item_data?.item,
          uniqueIds: item_data?.uniqueIds,
        }));

        setTotalUnits(item_data?.in_transit_units);
      } else {
        showToast({ type: ToastType.error, text: res.error });
        return null;
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching warehouse stock value",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updatePrice = async (requestedUnits: number) => {
    if (requestedUnits > totalUnits) {
      showToast({
        type: ToastType.warning,
        text: "Requested units exceed available stock.",
      });
      return;
    } else {
      setData((prevData) => ({
        ...prevData,
        unit_no: requestedUnits,
      }));
    }
  };

  const updatePriceOfIntransit = async (requestedUnits: number) => {
    if (requestedUnits > totalUnits) {
      showToast({
        type: ToastType.warning,
        text: "Requested units exceed available stock.",
      });
      return;
    } else {
      setData((prevUnits) => {
        return {
          ...prevUnits,
          unit_no: requestedUnits,
        };
      });
    }
  };

  const confirmedCount = useRef(0);

  useEffect(() => {
    if (data.uniqueIds) {
      confirmedCount.current = data.uniqueIds.filter(
        (id) => id.status === "Confirm"
      ).length;
    }
  }, [data.uniqueIds]);

  useEffect(() => {
    if (data.uniqueIds) {
      setData((prevData) => ({
        ...prevData,
        uniqueIds: data.uniqueIds,
      }));
    }
  }, [data.uniqueIds]);

  const handleSelectAll = () => {
    const newValue = !selectAll;
    setSelectAll(newValue);
    const newSelectedRows = new Array(data.uniqueIds?.length).fill(newValue);
    setSelectedRows(newSelectedRows);

    // Update uniqueIds status
    setData((prevData) => {
      const updatedUniqueIds = prevData.uniqueIds?.map((item) => ({
        ...item,
        status: newValue ? "Confirm" : "", // Set status for all
      }));

      return {
        ...prevData,
        uniqueIds: updatedUniqueIds,
        received_unit_no: newValue ? data.uniqueIds?.length || 0 : 0,
      };
    });
  };

  const handleRowSelect = (index: number) => {
    const newSelectedRows = [...selectedRows];
    newSelectedRows[index] = !newSelectedRows[index];
    setSelectedRows(newSelectedRows);

    // Count selected checkboxes
    const selectedCount = newSelectedRows.filter((val) => val).length;

    // Update received_unit_no immediately
    setData((prevData) => {
      const updatedUniqueIds = prevData.uniqueIds?.map((item, i) => ({
        ...item,
        status: newSelectedRows[i] ? "Confirm" : "", // Set status when checked
      }));

      return {
        ...prevData,
        uniqueIds: updatedUniqueIds,
        received_unit_no: selectedCount,
      };
    });

    // If all checkboxes are selected, update selectAll state
    setSelectAll(newSelectedRows.every((val) => val));
  };

  const onDone = () => {
    if (data.uniqueIds) {
      const hasEmpty = data.uniqueIds.some(
        (object) => !object.unique_id?.trim()
      );
      if (hasEmpty) {
        showToast({
          type: ToastType.error,
          text: "Please enter a valid Unique ID for all rows",
        });
        return;
      }
    }

    // Check for duplicates in previous entries
    const isDuplicate = (existingItems ?? []).some(
      (entry) =>
        entry.item?.item_code === data.item?.item_code && entry.id !== data.id // ignore current row being edited
    );

    if (isDuplicate) {
      showToast({
        type: ToastType.warning,
        text: "Item already added for same item code!!",
      });
      setEditable(true);
      return;
    }
    if (
      data.item?.subcategory !== "Finished Goods" &&
      data.item?.subcategory !== "IT and Electronic Hardware"
    ) {
      delete data.uniqueIds;
    } else if (data.uniqueIds) {
      for (const object of data.uniqueIds) {
        if (!object.unique_id) {
          showToast({
            type: ToastType.error,
            text: "Please enter a valid Item Unique ID",
          });
          setEditable(true);
          return; // This will stop further execution of the parent function
        }
      }
    }

    if (
      props.source === OutwardSource["In-transit"] &&
      data.inTransit_id === ""
    ) {
      showToast({
        type: ToastType.warning,
        text: "Please enter In-transit ID",
      });
      setEditable(true);
      return;
    }

    if (
      (data.item?.subcategory === "Finished Goods" ||
        data.item?.subcategory === "IT and Electronic Hardware") &&
      props.source === OutwardSource["In-transit"] &&
      confirmedCount.current !== data.unit_no
    ) {
      showToast({
        type: ToastType.warning,
        text: "Please enter the same number of confirmed Unique IDs as the Unit No",
      });
      setEditable(true);
      return;
    }

    if (
      !data.item ||
      !data.unit_no ||
      !props.from_warehouse?.name ||
      !props.source
    ) {
      showToast({
        type: ToastType.warning,
        text: "Please select Item, Warehouse, and source",
      });
      setEditable(true);
      return;
    }
    onSubmit(data);
  };

  const handleAddingUniqueIds = async () => {
    if (!Array.isArray(data.uniqueIds)) return;

    for (const object of data.uniqueIds) {
      if (!object.unique_id || object.unique_id.trim() === "") {
        showToast({
          type: ToastType.error,
          text: "Please enter a valid Unique ID, dont keep empty field!!",
        });
        return;
      } else if (!uniqueIds.current.includes(object.unique_id)) {
        showToast({
          type: ToastType.error,
          text: `Unique ID does not exist for ${object.unique_id}`,
        });
        return;
      }
    }

    // If loop completes without triggering any return
    setAddProduct(false);
  };

  const handleDownloadTemplate = () => {
    const headers = ["Unique ID", "Item Name", "Item Code"];

    const csvContent = [headers]
      .map((e) => e.map((v) => `"${v}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "product_list_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCSVUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validMimeTypes = ["text/csv", "application/vnd.ms-excel"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!validMimeTypes.includes(file.type) && fileExtension !== "csv") {
      showToast({ type: ToastType.error, text: "Only CSV files are allowed." });
      event.target.value = "";
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        const rawData = results.data as Record<string, string>[];

        if (rawData.length === 0) {
          showToast({
            type: ToastType.error,
            text: "CSV file is empty or invalid.",
          });
          return;
        }

        const parsedData = rawData.map((row) => {
          const trimmedRow: Record<string, string> = {};
          for (const key in row) {
            const trimmedKey = key.trim();
            const trimmedValue = row[key]?.trim() ?? "";
            trimmedRow[trimmedKey] = trimmedValue;
          }
          return trimmedRow;
        });

        const requiredHeaders = ["Item Code", "Unique ID", "Item Name"];
        const firstRow = parsedData[0];
        const missingHeaders = requiredHeaders.filter(
          (key) => !(key in firstRow)
        );

        if (missingHeaders.length > 0) {
          showToast({
            type: ToastType.error,
            text: `Missing column(s): ${missingHeaders.join(", ")}`,
          });
          return;
        }

        const formattedData: UniqueProductModel[] = parsedData.map((row) => ({
          unique_id: row["Unique ID"],
          item_name: row["Item Name"],
          item_code: row["Item Code"],
        }));

        // Check for duplicate Unique IDs within the uploaded CSV
        const seen: Set<string> = new Set();
        const duplicateInCSV: string[] = [];

        for (const item of formattedData) {
          if (!item.unique_id) continue;
          if (seen.has(item.unique_id)) {
            duplicateInCSV.push(item.unique_id);
          } else {
            seen.add(item.unique_id);
          }
        }

        if (duplicateInCSV.length > 0) {
          showToast({
            type: ToastType.error,
            text: `Duplicate Unique ID(s) in CSV: ${[
              ...new Set(duplicateInCSV),
            ].join(", ")}`,
          });
          return;
        }

        // Check against already existing IDs
        const duplicateExistingIds = formattedData
          .map((item) => item.unique_id!)
          .filter((id) => itemIds.includes(id));

        if (duplicateExistingIds.length > 0) {
          showToast({
            type: ToastType.error,
            text: `The following Unique ID(s) already exist: ${[
              ...new Set(duplicateExistingIds),
            ].join(", ")}`,
          });
          return;
        }

        const item_Code = data.item?.item_code ?? data.item_code;
        // Check that all item_codes match the current data.item_code
        const mismatchedItems = formattedData.filter(
          (item) => item.item_code !== item_Code
        );

        console.log("mismatchedItems:", mismatchedItems);

        if (mismatchedItems.length > 0) {
          const mismatchedCodes = [
            ...new Set(mismatchedItems.map((item) => item.item_code)),
          ].join(", ");
          showToast({
            type: ToastType.error,
            text: `Device ID(s) ${mismatchedCodes} cannot be Inwarded on Device ID ${
              data.item_code ?? "--"
            }`,
          });
          return;
        }

        const newUniqueIds = [...(data.uniqueIds ?? []), ...formattedData];

        const finalUniqueIds = Array.from(
          new Map(newUniqueIds.map((item) => [item.unique_id, item])).values()
        );

        setData((prev) => ({
          ...prev,
          uniqueIds: finalUniqueIds,
        }));

        showToast({
          type: ToastType.success,
          text: `${formattedData.length} new Unique ID(s) uploaded.`,
        });
      },
    });
  };

  const handleScanBarcodeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newChar = e.target.value;
  
    if (scanTimeout) {
      clearTimeout(scanTimeout);
    }
  
    if (newChar.length > 1) {
      const manualInput = newChar.trim();
      
      if (manualInput.length === 6 || manualInput.length === 8) {
        setScannedID(manualInput);
        setScanBuffer("");
        await getDeviceIDs(manualInput);
        return;
      } else if (manualInput.length < 6) {
        // Don't show error for manual input less than 6, just wait
        setScanBuffer(manualInput);
      } else {
        // More than 8 characters
        showToast({
          type: ToastType.error,
          text: "Please enter a valid scanned ID (6 or 8 characters)",
        });
        setScanBuffer("");
        e.target.value = "";
        return;
      }
    } else {

      //accumulate each character as buffer
      const updatedBuffer = scanBuffer + newChar;
      setScanBuffer(updatedBuffer);

      e.target.value = "";
  
      if (updatedBuffer.length > 8) {
        showToast({
          type: ToastType.error,
          text: "Please enter a valid scanned ID (6 or 8 characters)",
        });
        setScanBuffer("");
        return;
      }
      if (updatedBuffer.length === 8) {
        setScannedID(updatedBuffer);
        setScanBuffer("");
        await getDeviceIDs(updatedBuffer);
        return;
      }
    }
  
    const newTimeout = setTimeout(async () => {
      const currentBuffer = scanBuffer + (newChar.length === 1 ? newChar : "");
      console.log("Timeout triggered, current buffer:", currentBuffer);
      
      if (currentBuffer.length === 6) {
        // Accept 6-digit code after timeout (scanner finished)
        console.log("6-digit scan accepted after timeout:", currentBuffer);
        setScannedID(currentBuffer);
        setScanBuffer("");
        await getDeviceIDs(currentBuffer);
      } else if (currentBuffer.length > 0 && currentBuffer.length < 6) {
        // Only show error if less than 6 digits
        showToast({
          type: ToastType.error,
          text: "Please enter a valid scanned ID (6 or 8 characters)",
        });
        setScanBuffer("");
      } else if (currentBuffer.length > 6 && currentBuffer.length < 8) {
        // Between 6-8 but not exactly 6 or 8
        showToast({
          type: ToastType.error,
          text: "Please enter a valid scanned ID (6 or 8 characters)",
        });
        setScanBuffer("");
      }
    }, 300); // Shorter timeout to be more responsive
  
    setScanTimeout(newTimeout);
  };
  const downloadCsv = () => {
    const ids = uniqueIds.current || [];
    if (!ids.length) {
      showToast({ type: ToastType.info, text: "No IDs to download." });
      return;
    }

    setLoading(true);
    setTimeout(() => {
      const header = ["Device ID"];
      const rows = ids.map((id) => [id]);
      const csvRows = [header, ...rows];
      const csvString = csvRows.map((r) => r.join(",")).join("\n");
      const csvContent = "\uFEFF" + csvString;

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "device_ids.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setLoading(false);
    }, 100); // Slight delay to allow UI (like spinner) to render
  };


  const buttonClass =
    "border rounded-lg px-4 py-2 hover:bg-gray-800 hover:text-white";

  return (
    <div>
      {/* for in-transit option */}
      {addProduct1 && (
        <ModalDialog
          show={addProduct1}
          title={"Product Details"}
          onClose={() => {
            setAddProduct1(false);
            setEditable(true);
          }}
        >
          <div className="bg-white p-6 w-96">
            <div className="flex justify-between">
              <h2 className="text-md font-semibold mb-4">Product List</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-300">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="p-2 border">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className="p-2 border">Unique Id</th>
                    <th className="p-2 border">Item Name</th>
                  </tr>
                </thead>

                <tbody>
                  {(data.uniqueIds || []).map((row: any, index: number) => (
                    <tr key={row.id} className="text-center">
                      <td className="p-2 border">
                        <input
                          type="checkbox"
                          checked={selectedRows[index]}
                          onChange={() => handleRowSelect(index)}
                        />
                        <span className="ml-2">Confirm</span>
                      </td>
                      <td className="p-2 border">
                        <input
                          type="text"
                          value={row.unique_id || ""}
                          className="p-1 border rounded w-full focus:outline-none"
                          placeholder="Enter Unique Id"
                        />
                      </td>
                      <td className="p-2 border">{row.item_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end mt-3 sticky bottom-0 bg-white z-10 p-2 border-t">
              <button
                onClick={() => {
                  console.log("Done button clicked");
                  setAddProduct1(false);
                }}
                className="bg-green-600 text-white p-1 rounded-md"
              >
                Done
              </button>
            </div>
          </div>
        </ModalDialog>
      )}

      {choiceModal && (
        <ModalDialog
          show={choiceModal}
          title={
            scan
              ? "Scan Barcode"
              : addProduct
              ? "Upload CSV"
              : "Choose Upload Method"
          }
          onClose={() => {
            setChoiceModal(false);
            setScan(false);
            setAddProduct(false);
            setScannedID("");
            setScannedDevices([]);
            scannedBufferRef.current = "";
            if (inputTimeoutRef.current) {
              clearTimeout(inputTimeoutRef.current);
            }
          }}
        >
          {/* Step 1: Show scan section if selected */}
          {scan ? (
            <section>
              <div className="bg-gray-50 p-3 m-2 border rounded-lg">
                <input
                  type="text"
                  value={scannedID}
                  placeholder="Scan Barcode"
                  className="font-semibold h-full w-full"
                  ref={scannedInputFieldRef}
                  disabled={loading}
                  autoComplete="off"
                  autoFocus
                  onChange={handleScanBarcodeChange}
                  // onInput={(e) => {
                  //   // onInput fires synchronously BEFORE React batches updates
                  //   // This is critical for barcode scanners that send data very quickly
                  //   const inputElement = e.target as HTMLInputElement;
                  //   const value = inputElement.value;

                  //   console.log("onInput fired:", value, "length:", value.length);

                  //   // Store in buffer immediately (synchronous, no batching)
                  //   scannedBufferRef.current = value;

                  //   // Update React state for visual feedback
                  //   setScannedID(value);

                  //   // Clear any existing timeout
                  //   if (inputTimeoutRef.current) {
                  //     clearTimeout(inputTimeoutRef.current);
                  //   }

                  //   // Set timeout to reset after scanning completes
                  //   // Barcode scanners send data in 5-20ms, so 100ms is safe
                  //   inputTimeoutRef.current = setTimeout(() => {
                  //     // If we have exactly 8 characters, it might be a complete barcode
                  //     // but we'll wait for Enter key to process it
                  //   }, 100);
                  // }}
                  // onKeyDown={async (e) => {
                  //   // Handle Enter key (QR scanners typically send Enter after the data)
                  //   if (e.key === "Enter") {
                  //     e.preventDefault();
                  //     e.stopPropagation();

                  //     // Use buffer value (most up-to-date, avoids React batching issues)
                  //     const currentValue = scannedBufferRef.current.trim();

                  //     console.log("Enter pressed, buffer value:", currentValue, "length:", currentValue.length);

                  //     if (currentValue && !isProcessingScan.current) {
                  //       // Validate it's exactly 8 characters (your barcode format)
                  //       if (currentValue.length === 8) {
                  //         isProcessingScan.current = true;

                  //         // Clear timeout
                  //         if (inputTimeoutRef.current) {
                  //           clearTimeout(inputTimeoutRef.current);
                  //           inputTimeoutRef.current = null;
                  //         }

                  //         await getDeviceIDs(currentValue);
                  //         isProcessingScan.current = false;

                  //         // Clear input after processing
                  //         setScannedID("");
                  //         scannedBufferRef.current = "";

                  //         // Refocus for next scan
                  //         setTimeout(() => {
                  //           if (scannedInputFieldRef.current && scan && choiceModal) {
                  //             scannedInputFieldRef.current.focus();
                  //           }
                  //         }, 100);
                  //       } else {
                  //         console.warn("Invalid barcode length:", currentValue.length, "expected 8");
                  //       }
                  //     }
                  //   }
                  // }}
                  // onBlur={(e) => {
                  //   // Prevent losing focus during scanning
                  //   if (scan && choiceModal && !loading) {
                  //     setTimeout(() => {
                  //       if (scannedInputFieldRef.current) {
                  //         scannedInputFieldRef.current.focus();
                  //       }
                  //     }, 50);
                  //   }
                  // }}
                />
                {/* <Scanner
                    onScan={(detected: IDetectedBarcode[]) => {
                      if (detected.length > 0) {
                        setScannedCode(detected[0].rawValue);
                      }
                    }}
                    onError={(err) => {
                      console.error("QR scan error:", err);
                    }}
                    formats={["qr_code"]}
                    scanDelay={500}
                  /> */}
              </div>
              <div className="overflow-auto border rounded-lg">
                <table className="w-full text-sm text-left table-auto border-collapse">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-4 py-2">Device ID</th>
                      <th className="px-4 py-2">Item Name</th>
                      <th className="px-4 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scannedDevices.map((device) => (
                      <tr
                        key={device.unique_id}
                        className={`border-b hover:bg-gray-50 ${
                          device.inventory_found === false ? "bg-gray-200" : ""
                        }`}
                      >
                        <td className="px-4 py-2">{device.unique_id}</td>
                        <td className="px-4 py-2">{device.item_name}</td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => handleDeleteDevice(device.unique_id)}
                            className="text-red-600 hover:text-red-800 font-medium"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                    {scannedDevices.length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className="text-center py-3 text-gray-500"
                        >
                          No scanned devices.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 my-4">
                <button
                  className={buttonClass}
                  onClick={() => {
                    setScan(false);
                    setScannedID("");
                  }}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className={buttonClass}
                  onClick={() => {
                    handleAddingDeviceIds();
                  }}
                >
                  {props.existingItems ? "Update" : "Create"}
                </button>
              </div>
            </section>
          ) : addProduct ? (
            // Step 2: Show CSV upload section if selected
            <div className="bg-white p-6 rounded-md shadow-lg w-96">
              {/* <p>Available Devices: {(uniqueIds.current || []).join(", ")}</p> */}
              <div className="m-1 border rounded p-1">
                <p>Get Available Devices here in csv..</p>

                <label
                  onClick={downloadCsv}
                  className="bg-myPrimaryColor text-white rounded-lg px-3 py-1 text-sm cursor-pointer duration-300 m-1"
                >
                  Available Devices
                </label>
              </div>
              <div className="flex justify-between">
                <h2 className="text-md font-semibold mb-4">Add Product List</h2>

                {/* <div className="">
                  <label
                    onClick={handleDownloadTemplate}
                    className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 m-1"
                  >
                    Template
                  </label>
                  <label className="bg-myPrimaryColor text-white p-1 rounded-md cursor-pointer">
                    Upload
                    <input
                      type="file"
                      accept=".csv"
                      ref={fileInputRef}
                      onChange={handleCSVUpload}
                      className="hidden"
                    />
                  </label>
                </div> */}
              </div>
              <div className="overflow-x-auto">
                <div className="overflow-y-auto max-h-60 border border-gray-300">
                  <table className="w-full border border-gray-300">
                    {/* Table Head */}
                    <thead className="bg-gray-200">
                      <tr>
                        <th className="p-2 border">Unique Id</th>
                        <th className="p-2 border">Item Name</th>
                        <th className="p-2 border">Actions</th>
                      </tr>
                    </thead>

                    {/* Table Body */}
                    <tbody>
                      {/* {rows.map((row: any, index: any) => ( */}
                      {(data.uniqueIds || []).map((row: any, index) => (
                        <tr key={row.id} className="text-center">
                          <td className="p-2 border">
                            <input
                              type="text"
                              value={row.unique_id || ""}
                              onChange={(e) =>
                                handleInputChange(index, e.target.value)
                              }
                              className="p-1 border rounded w-full focus:outline-none"
                              placeholder="Enter Unique Id"
                            />
                          </td>

                          <td className="p-2 border">{row.item_name}</td>
                          <td className="p-2 border">
                            <div
                              onClick={() => handleDelete(index)}
                              className="bg-red-100 p-1 rounded text-red-800 hover:scale-110 transform duration-200 cursor-pointer inline-block"
                            >
                              <Icon path={mdiDelete} className="h-4 w-4" />
                            </div>
                          </td>
                        </tr>
                      ))}

                      {/* Add New Row Button */}
                      {/* <tr>
                        <td colSpan={3} className="p-2 border text-center">
                          <div
                            onClick={handleAdd}
                            className="bg-blue-100 p-1 rounded text-blue-800 hover:scale-110 transform duration-200 cursor-pointer inline-flex items-center space-x-1"
                          >
                            <Icon path={mdiPlus} className="h-4 w-4" />
                            <span>Add Row</span>
                          </div>
                        </td>
                      </tr> */}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end items-center mt-4">
                  <button
                    className="bg-myPrimaryColor text-white p-1 rounded-md mt-4 "
                    onClick={() => {
                      handleAddingUniqueIds();
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
              <div className="flex justify-end gap-2 my-4">
                <button
                  className={buttonClass}
                  onClick={() => {
                    setAddProduct(false);
                  }}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className={buttonClass}
                  onClick={() => {
                    handleAddingUniqueIds();
                  }}
                >
                  {props.existingItems ? "Update" : "Create"}
                </button>
              </div>
            </div>
          ) : (
            // Step 3: Show initial method choice
            <div className="flex justify-center gap-6 py-4">
              <button
                type="button"
                className={buttonClass}
                onClick={() => {
                  setScan(true);
                  setAddProduct(false);
                  setScannedID("");
                  setScannedDevices([]);
                }}
              >
                Scan ID
              </button>
              <button
                type="button"
                title="Required fields - Item Code, Unique ID, Item Name"
                className={buttonClass}
                onClick={() => {
                  setAddProduct(true);
                  setScan(false);
                }}
              >
                Upload CSV
              </button>
            </div>
          )}
        </ModalDialog>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onDone();
        }}
        className="bg-white p-1"
      >
        {props.source === OutwardSource["Stock inventory"] && (
          <div className="grid grid-cols-9 gap-1 items-center">
            <div>
              <input
                className="text-left p-1 focus:outline-none border rounded w-full"
                placeholder="item-code"
                disabled
                value={data.item.item_code}
                readOnly
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>

            <div className="col-span-2">
              <NewItemSearchByIdField
                disabled={!editable || props.disabled}
                value={data.item}
                item_ids={itemIds}
                onSelect={handleItemSelect}
                // onSelect={(d: ItemModelV2) => {
                //   // setSelectedItem(data.item);
                //   setData((o) => ({
                //     ...o,
                //     item: d,
                //     item_id: d.id,
                //     item_code: d.item_code,
                //   }));
                // }}
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>
            <div>
              <input
                className="text-left p-1 focus:outline-none border rounded w-full"
                placeholder="unit"
                disabled
                value={data.item?.unit?.symbol}
                readOnly
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>
            <div>
              <input
                value={data.unit_no || ""}
                disabled={!editable || props.disabled}
                onChange={(e) => {
                  const unitNo = Number(e.target.value) || 0;
                  // setData((prevData) => ({
                  //   ...prevData,
                  //   unit_no: unitNo,
                  // }));
                  if (props.from_warehouse) {
                    updatePrice(unitNo);
                  }
                }}
                // disabled={!editable || props.disabled}
                type="text"
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">
                Avail Units:{totalUnits} {""}{" "}
                {props.outward_type === OutwardType["Finished Good Sale"] && (
                  <> Order Units:{data.qty}</>
                )}
              </span>
            </div>
            <div>
              {data.item?.type === "Product" &&
              (data.item?.subcategory === "Finished Goods" ||
                data.item?.subcategory === "IT and Electronic Hardware") ? (
                <div
                  onClick={() => {
                    // Ensure uniqueIds array exists before opening modal
                    if (!data.uniqueIds) {
                      setData((prevData) => ({
                        ...prevData,
                        uniqueIds: [],
                      }));
                    }
                    setChoiceModal(true);
                  }}
                  className="text-left p-1 focus:outline-none border rounded text-blue-800 w-full cursor-pointer"
                >
                  <div className="flex items-center space-x-2">
                    <Icon path={mdiPlus} className="h-6 w-4" />

                    {/* <span>
                      {(data.uniqueIds?.length ?? 0) > 0
                        ? `${data.uniqueIds?.length ?? 0} items added`
                        : "Add/Upload"}
                    </span> */}
                    <span>
                      {(data.uniqueIds?.filter((u) => u?.unique_id)?.length ??
                        0) > 0
                        ? `${
                            data.uniqueIds?.filter((u) => u?.unique_id)
                              ?.length ?? 0
                          } devices added`
                        : "Add/Upload"}
                    </span>
                  </div>
                </div>
              ) : (
                <input
                  className="text-left p-1 focus:outline-none border rounded w-full"
                  placeholder="unique"
                  disabled={true}
                  onClick={() => {}}
                />
              )}
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>

            <div>
              <input
                value={data.price || ""}
                disabled={!editable || props.disabled}
                onChange={(e) => {
                  const price = Number(e.target.value) || 0;
                  setData((prevData) => ({
                    ...prevData,
                    price: price,
                  }));
                }}
                // disabled={!editable || props.disabled}
                type="text"
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">
                {/* Price of latest:{totalUnits} */}&nbsp;
              </span>
            </div>
            <div>
              <input
                value={data.price * data.unit_no || ""}
                disabled={!editable || props.disabled}
                // disabled={!editable || props.disabled}
                type="text"
                readOnly
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>
            <div className="flex gap-2 top-0 items-center justify-center">
              {props.component ? (
                !props.disabled && (
                  <div>
                    <div className="flex justify-center gap-2">
                      {editable ? (
                        <>
                          <div
                            onClick={() => {
                              if (!window.confirm("Are you sure!")) return;
                              onDone();
                              setEditable(false);
                            }}
                            className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200"
                          >
                            <Icon path={mdiCheck} className="h-4 w-4" />
                          </div>
                          <div
                            onClick={() => {
                              setData(
                                props.component || itemStockOutwardDefaultValue
                              );
                              setEditable(false);
                            }}
                            className="bg-gray-100 p-1 rounded text-gray-800 hover:scale-110 transform duration-200"
                          >
                            <Icon path={mdiClose} className="h-4 w-4" />
                          </div>
                        </>
                      ) : (
                        (data.voucher_id || isCreatePath) && (
                          <div
                            onClick={() => {
                              setEditable(true);
                              setMdiCheck(false);
                            }}
                            className="bg-blue-100 p-1 rounded text-blue-800 hover:scale-110 transform duration-200"
                          >
                            <Icon path={mdiPencil} className="h-4 w-4" />
                          </div>
                        )
                      )}
                      {/* {data.voucher_id && ( */}
                      <div
                        onClick={() =>
                          props.onDelete && props.onDelete(props.component!)
                        }
                        className="bg-red-100 p-1 rounded text-red-800 hover:scale-110 transform duration-200"
                      >
                        <Icon path={mdiDelete} className="h-4 w-4" />
                      </div>
                      {/* )} */}
                    </div>
                    <span className="text-xs text-gray-500">&nbsp;</span>
                  </div>
                )
              ) : (
                <div>
                  <div
                    onClick={() => {
                      setEditable(false);
                      onDone();
                    }}
                    className="bg-blue-100 p-1 rounded text-blue-800 hover:scale-110 transform duration-200"
                  >
                    <span> + Add</span>
                  </div>
                  <span className="text-xs text-gray-500">&nbsp;</span>
                </div>
              )}
            </div>
          </div>
        )}
        {props.source === OutwardSource["In-transit"] && (
          <div className="grid grid-cols-10 gap-1 items-center">
            <div>
              <input
                value={data.inTransit_id || ""}
                disabled={!editable || props.disabled}
                onChange={(e) => {
                  const id = e.target.value;
                  setData((prevData) => ({
                    ...prevData,
                    inTransit_id: id,
                  }));
                }}
                // disabled={!editable || props.disabled}
                type="text"
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>
            <div>
              <input
                value={data.stock_transfer_id || ""}
                disabled={!editable || props.disabled}
                // onChange={(e) => {
                //   const id = e.target.value;
                //   setData((prevData) => ({
                //     ...prevData,
                //     inTransit_id: id,
                //   }));
                // }}
                // disabled={!editable || props.disabled}
                type="text"
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>
            <div>
              <input
                className="text-left p-1 focus:outline-none border rounded w-full"
                placeholder="item-code"
                disabled
                value={data.item_code}
                readOnly
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>

            <div className="col-span-2">
              <POItemSearchFieldV2
                disabled={!editable || props.disabled}
                value={data.item}
                // item_ids={itemIds}
                onSelect={(d: ItemModelV2) => {
                  // setSelectedItem(data.item);
                  setData((o) => ({
                    ...o,
                    item: d,
                    item_id: d.id,
                    item_code: d.item_code,
                  }));
                }}
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>

            <div>
              {data.item?.type === "Product" &&
              (data.item?.subcategory === "Finished Goods" ||
                data.item?.subcategory === "IT and Electronic Hardware") ? (
                <div
                  onClick={() => {
                    // Ensure uniqueIds array exists before opening modal
                    if (!data.uniqueIds) {
                      setData((prevData) => ({
                        ...prevData,
                        uniqueIds: [],
                      }));
                    }
                    setAddProduct1(true);
                  }}
                  className="text-left p-1 focus:outline-none border rounded text-blue-800 w-full cursor-pointer"
                >
                  <div className="flex items-center space-x-2">
                    <Icon path={mdiPlus} className="h-6 w-4" />
                    <span>
                      {(confirmedCount.current ?? 0) > 0
                        ? `${confirmedCount.current ?? 0} items added`
                        : "Add/Upload"}
                    </span>
                    {/* <span>
                      {(data.uniqueIds?.filter((u) => u.status === "Confirm")
                        ?.length ?? 0) > 0
                        ? `${
                            data.uniqueIds?.filter(
                              (u) => u.status === "Confirm"
                            )?.length ?? 0
                          } devices added`
                        : "Add/Upload"}
                    </span> */}
                  </div>
                </div>
              ) : (
                <input
                  className="text-left p-1 focus:outline-none border rounded w-full"
                  placeholder="unique"
                  // value={}
                  onClick={() => {}}
                />
              )}
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>
            {/* {props.source === OutwardSource["Stock inventory"] && ( */}
            <div>
              <input
                value={data.unit_no || ""}
                disabled={!editable || props.disabled}
                onChange={(e) => {
                  const unitNo = Number(e.target.value) || 0;
                  // setData((prevData) => ({
                  //   ...prevData,
                  //   unit_no: unitNo,
                  // }));
                  if (props.from_warehouse) {
                    updatePriceOfIntransit(unitNo);
                  }
                }}
                // disabled={!editable || props.disabled}
                type="text"
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">
                Intransit Units:{totalUnits}
              </span>
            </div>
            {/* )} */}
            {/* {props.source === OutwardSource["In-transit"] && (
            <div>
              <input
                value={data.unit_no || ""}
                disabled={!editable || props.disabled}
                onChange={(e) => {
                  const unitNo = Number(e.target.value) || 0;
                  // setData((prevData) => ({
                  //   ...prevData,
                  //   unit_no: unitNo,
                  // }));
                  if (props.from_warehouse) {
                    updatePriceBasedOnUnits(unitNo);
                  }
                }}
                // disabled={!editable || props.disabled}
                type="text"
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">
                Avail Units:{totalUnits}
              </span>
            </div>
          )} */}
            <div>
              <input
                value={data.price || ""}
                disabled={!editable || props.disabled}
                onChange={(e) => {
                  const price = Number(e.target.value) || 0;
                  setData((prevData) => ({
                    ...prevData,
                    price: price,
                  }));
                }}
                // disabled={!editable || props.disabled}
                type="text"
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">
                {/* Price of latest:{totalUnits} */}&nbsp;
              </span>
            </div>
            <div>
              <input
                value={data.price * data.unit_no || ""}
                disabled={!editable || props.disabled}
                // disabled={!editable || props.disabled}
                type="text"
                readOnly
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>
            <div className="flex gap-2 top-0 items-center justify-center">
              {props.component ? (
                !props.disabled && (
                  <div className="flex justify-center gap-2">
                    {editable ? (
                      <>
                        <div
                          onClick={() => {
                            if (!window.confirm("Are you sure!")) return;
                            onDone();
                            setEditable(false);
                          }}
                          className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200"
                        >
                          <Icon path={mdiCheck} className="h-4 w-4" />
                        </div>
                        <div
                          onClick={() => {
                            setData(
                              props.component || itemStockOutwardDefaultValue
                            );
                            setEditable(false);
                          }}
                          className="bg-gray-100 p-1 rounded text-gray-800 hover:scale-110 transform duration-200"
                        >
                          <Icon path={mdiClose} className="h-4 w-4" />
                        </div>
                      </>
                    ) : (
                      (data.voucher_id || isCreatePath) && (
                        <div
                          onClick={() => {
                            setEditable(true);
                            setMdiCheck(false);
                          }}
                          className="bg-blue-100 p-1 rounded text-blue-800 hover:scale-110 transform duration-200"
                        >
                          <Icon path={mdiPencil} className="h-4 w-4" />
                        </div>
                      )
                    )}
                    {/* {data.voucher_id && ( */}
                    <div
                      onClick={() =>
                        props.onDelete && props.onDelete(props.component!)
                      }
                      className="bg-red-100 p-1 rounded text-red-800 hover:scale-110 transform duration-200"
                    >
                      <Icon path={mdiDelete} className="h-4 w-4" />
                    </div>
                    {/* )} */}
                  </div>
                )
              ) : (
                <div
                  onClick={() => {
                    setEditable(false);
                    onDone();
                  }}
                  className="bg-blue-100 p-1 rounded text-blue-800 hover:scale-110 transform duration-200"
                >
                  {/* <Icon path={mdiPlus} className="h-4 w-4" /> */}
                  <span> + Add</span>
                </div>
              )}
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ItemRowOutward;
